// API 服务层 - 集中管理所有网络请求
import { stocks as stockApi } from "stock-api";

// API 配置
const API_BASE = "/api/v1";

// 类型定义
export interface ApiUser {
  phone: string;
  token: string;
}

export interface ApiKline {
  low: number;
  date: string;
  open: number;
  high: number;
  close: number;
  volume: number;
  percent: number;
}

export interface ApiStock {
  id: number;
  code: string;
  name: string;
  price?: number;
  klines?: ApiKline[];
  exchange: string;
  quantity?: number;
  costPrice?: number;
  // 信号列表字段（多空信号 / 神奇9转）
  signalDate?: string;
  signalPrice?: number;
  maxPercent?: number;
  totalPercent?: number;
}

// Job 类型
export interface ApiJob {
  id: number;
  cron: string;
  name: string | null;
  task: { [key: string]: string[] };
  result: string | null;
  status: "running" | "stopped";
  at_created?: string;
  at_updated?: string;
}

export interface ApiPagination {
  page: number;
  total: number;
  pageSize: number;
}

export interface ApiJobTask {
  name: string;
  description?: string;
}

export interface ApiResponse {
  code: number;
  data?: {
    jobs?: ApiJob[];
    user?: ApiUser;
    tasks?: string[];
    stock?: ApiStock;
    stocks?: ApiStock[];
    klines?: ApiKline[];
    noStocks?: { code: string, name: string }[];
    pagination?: ApiPagination;
  };
  message?: string;
}

// 获取认证头
const getAuthHeaders = (token: string | null | undefined): Record<string, string> => {
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
};

// 401 回调：token 失效（过期/被顶下线）时由 AuthContext 注册，用于自动登出
let unauthorizedHandler: (() => void) | null = null;
export function onUnauthorized(handler: (() => void) | null) {
  unauthorizedHandler = handler;
}

// 处理 API 响应
const handleResponse = async (url: string, res: Response): Promise<ApiResponse> => {
  if (res.status === 401) {
    unauthorizedHandler?.();
    throw new Error("未登录或登录已过期");
  }
  if (!res.ok) {
    throw new Error(`Fetch ${url} error! status: ${res.status}`);
  }
  const data: ApiResponse = await res.json();
  return data;
};

// API 服务类
export class ApiService {
  private token: string | null | undefined;

  constructor(token?: string | null | undefined) {
    this.token = token;
  }

  // 更新认证令牌
  updateToken(token?: string | null | undefined) {
    this.token = token;
  }

  // 获取K线数据
  async getKlines(code: string): Promise<ApiResponse> {
    const url = `${API_BASE}/stock/klines?code=${code}`;
    const res = await fetch(url, {
      headers: {
        ...getAuthHeaders(this.token),
      },
    });
    return await handleResponse(url, res);
  }

  // 获取股票实时价格
  async getStockPrice(code: string): Promise<{ price: number, percent: number }> {
    const stock = await stockApi.auto.getStock(code);
    // 返回实时价格、涨跌幅
    return { price: stock.now, percent: stock.percent }
  }

  // 获取看多、看空的股票
  async getDKStocks(
    trend: 'duo' | 'kong',
    scope: 'all' | 'favorites',
    indicator: 'MACD' | 'KDJ' | 'RSI',
    page: number,
    pageSize: number
  ): Promise<ApiResponse> {
    const url = `${API_BASE}/stock/dk?trend=${trend}&scope=${scope}&indicator=${indicator}&page=${page}&pageSize=${pageSize}`;
    const res = await fetch(url, {
      headers: {
        ...getAuthHeaders(this.token),
      },
    });
    return await handleResponse(url, res);
  }

  // 获取神奇9转的股票
  async getUpdownStocks(
    days: number,
    trend: 'up' | 'down',
    scope: 'all' | 'favorites',
    page: number,
    pageSize: number
  ): Promise<ApiResponse> {
    const url = `${API_BASE}/stock/updown?days=${days}&trend=${trend}&scope=${scope}&page=${page}&pageSize=${pageSize}`;
    const res = await fetch(url, {
      headers: {
        ...getAuthHeaders(this.token),
      },
    });
    return await handleResponse(url, res);
  }

  // 获取持仓的股票
  async getAccountStocks(page: number, pageSize: number = 12): Promise<ApiResponse> {
    const url = `${API_BASE}/account/stocks?page=${page}&pageSize=${pageSize}`;
    const res = await fetch(url, {
      headers: {
        ...getAuthHeaders(this.token),
      },
    });
    return await handleResponse(url, res);
  }

  // 添加到自选
  async addToFavorites(stockId: number, price: number, quantity: number): Promise<ApiResponse> {
    const url = `${API_BASE}/account/stocks`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(this.token),
      },
      body: JSON.stringify({ price, stockId, quantity }),
    });
    return await handleResponse(url, res);
  }

  // 更新持仓（加仓/减仓）
  async updateAccountStock(stockId: number, quantity: number, price: number, action: "add" | "reduce"): Promise<ApiResponse> {
    const url = `${API_BASE}/account/stocks`;
    const res = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(this.token),
      },
      body: JSON.stringify({ stockId, quantity, price, action }),
    });
    return await handleResponse(url, res);
  }

  // 从自选删除
  async removeFromFavorites(stockId: number): Promise<ApiResponse> {
    const url = `${API_BASE}/account/stocks`;
    const res = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(this.token),
      },
      body: JSON.stringify({ stockId }),
    });

    return await handleResponse(url, res);
  }

  // 搜索股票
  async searchStocks(query: string): Promise<ApiResponse> {
    const url = `${API_BASE}/stock/search?query=${encodeURIComponent(query)}`;
    const res = await fetch(url, {
      headers: {
        ...getAuthHeaders(this.token)
      },
    });

    // 1. 数据库中查询到了
    const result = await handleResponse(url, res);
    if (result.data?.stocks && result.data.stocks?.length > 0) return result;

    // 2. 数据库中未找到，尝试上游搜索
    const upstreamResults = await stockApi.auto.searchStocks(query);
    if (upstreamResults.length > 0) {
      const noStocks = upstreamResults.map((stock) => ({
        code: stock.code,
        name: stock.name,
      }));
      // TODO 需要前端处理这种数据格式，代表未入库的股票，提示用户入库。
      return { code: 200, data: { noStocks } };
    }
    // 3. 全部未找到
    return { code: 200, data: { stocks: [] } };
  }

  // 获取用户信息
  async getProfile(): Promise<ApiResponse> {
    const url = `${API_BASE}/account/profile`;
    const res = await fetch(url, {
      headers: {
        ...getAuthHeaders(this.token)
      },
    });
    return handleResponse(url, res);
  }

  // 登录
  async login(phone: string, password: string): Promise<ApiResponse> {
    const url = `${API_BASE}/account/login`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ phone, password }),
    });
    return await handleResponse(url, res);
  }

  // 注册
  async register(phone: string, captcha: string, password: string): Promise<ApiResponse> {
    const url = `${API_BASE}/account/register`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ phone, captcha, password }),
    });
    return await handleResponse(url, res);
  }

  // 重置密码
  async resetPassword(phone: string, captcha: string, password: string): Promise<ApiResponse> {
    const url = `${API_BASE}/account/password`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ phone, captcha, password }),
    });

    return handleResponse(url, res);
  }

  // 发送验证码
  async sendCaptcha(phone: string): Promise<ApiResponse> {
    const url = `${API_BASE}/account/captcha`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ phone }),
    });
    return await handleResponse(url, res);
  }

  // 登出
  async logout(): Promise<void> {
    const url = `${API_BASE}/account/logout`;
    await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(this.token)
      },
      body: JSON.stringify({})
    });
  }

  // 获取任务列表
  async getJobList(page: number = 1, pageSize: number = 10): Promise<ApiResponse> {
    const url = `${API_BASE}/job?page=${page}&pageSize=${pageSize}`;
    const res = await fetch(url, {
      headers: { ...getAuthHeaders(this.token) },
    });
    return await handleResponse(url, res);
  }

  // 创建任务
  async createJob(data: { cron: string; name: string; task: { [key: string]: string[] }; status?: string }): Promise<ApiResponse> {
    const url = `${API_BASE}/job`;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getAuthHeaders(this.token) },
      body: JSON.stringify(data),
    });
    return await handleResponse(url, res);
  }

  // 更新任务
  async updateJob(data: { id: number; cron?: string; name?: string | null; task?: any; status?: string; result?: string | null }): Promise<ApiResponse> {
    const url = `${API_BASE}/job`;
    const res = await fetch(url, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...getAuthHeaders(this.token) },
      body: JSON.stringify(data),
    });
    return await handleResponse(url, res);
  }

  // 删除任务
  async deleteJob(id: number): Promise<ApiResponse> {
    const url = `${API_BASE}/job?id=${id}`;
    const res = await fetch(url, {
      method: "DELETE",
      headers: { ...getAuthHeaders(this.token) },
    });
    return await handleResponse(url, res);
  }

  // 控制任务 (启动/停止)
  async controlJob(data: { id: number; action: "start" | "stop" }): Promise<ApiResponse> {
    const url = `${API_BASE}/job`;
    const res = await fetch(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...getAuthHeaders(this.token) },
      body: JSON.stringify(data),
    });
    return await handleResponse(url, res);
  }

  // 获取任务模块列表
  async getJobTasks(): Promise<string[]> {
    const url = `${API_BASE}/job/task`;
    const res = await fetch(url, {
      headers: { ...getAuthHeaders(this.token) },
    });
    const data = await handleResponse(url, res);
    return data.data?.tasks ?? [];
  }
}

// 创建 API 服务实例
let apiServiceInstance: ApiService | null = null;

export function getApiService(token?: string | null | undefined): ApiService {
  if (!apiServiceInstance) {
    apiServiceInstance = new ApiService(token);
  } else if (token !== undefined) {
    apiServiceInstance.updateToken(token);
  }
  return apiServiceInstance;
}

// 重置 API 服务实例（用于测试）
export function resetApiService() {
  apiServiceInstance = null;
}
